import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Search, Users, Filter, SortAsc, SortDesc, RefreshCw, 
  ChevronLeft, ChevronRight, AlertTriangle, CheckCircle,
  BookOpen, Clock, Activity, Eye, Download, Loader2
} from 'lucide-react';
import ModalAluno from './ModalAluno';

// Cores de classificação
const CLASSIFICATION_STYLES = {
  excelente: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', badge: 'bg-green-500' },
  estavel: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', badge: 'bg-blue-500' },
  atencao: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30', badge: 'bg-orange-500' },
  critico: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', badge: 'bg-red-500' }
};

const RISCO_STYLES = {
  baixo: { bg: 'bg-green-500', text: 'Baixo' },
  medio: { bg: 'bg-yellow-500', text: 'Médio' },
  alto: { bg: 'bg-orange-500', text: 'Alto' },
  critico: { bg: 'bg-red-500', text: 'Crítico' }
};

// Componente de Card do Aluno
function AlunoCard({ aluno, onView }) {
  const classStyle = CLASSIFICATION_STYLES[aluno.classificacao] || CLASSIFICATION_STYLES.estavel;
  const riscoStyle = RISCO_STYLES[aluno.risco_reprovacao] || RISCO_STYLES.baixo;
  
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-blue-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };
  
  return (
    <Card 
      className={`bg-slate-800/50 border-purple-500/20 hover:border-purple-500/40 transition-all cursor-pointer group ${classStyle.border}`}
      onClick={() => onView(aluno)}
      data-testid={`aluno-card-${aluno.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="relative">
            {aluno.foto ? (
              <img 
                src={aluno.foto} 
                alt={aluno.nome}
                className="w-14 h-14 rounded-full object-cover border-2 border-purple-500/30"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-600 to-purple-400 flex items-center justify-center text-white text-xl font-bold">
                {aluno.nome?.charAt(0) || '?'}
              </div>
            )}
            <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${classStyle.badge} border-2 border-slate-800 flex items-center justify-center`}>
              {aluno.classificacao === 'excelente' && <CheckCircle className="w-3 h-3 text-white" />}
              {aluno.classificacao === 'critico' && <AlertTriangle className="w-3 h-3 text-white" />}
            </div>
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-white font-semibold truncate group-hover:text-purple-300 transition-colors">
                  {aluno.nome}
                </h3>
                <p className="text-purple-300/60 text-sm">{aluno.turma}</p>
                <p className="text-purple-300/40 text-xs">Mat: {aluno.matricula}</p>
              </div>
              <div className="text-right">
                <div className={`text-2xl font-bold ${getScoreColor(aluno.score_total)}`}>
                  {Math.round(aluno.score_total)}
                </div>
                <p className="text-purple-300/50 text-xs">Score</p>
              </div>
            </div>
            
            {/* Scores Mini */}
            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-1">
                <BookOpen className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-sm text-purple-200">{Math.round(aluno.score_academico)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-green-400" />
                <span className="text-sm text-purple-200">{Math.round(aluno.score_frequencia)}%</span>
              </div>
              <div className="flex items-center gap-1">
                <Activity className="w-3.5 h-3.5 text-purple-400" />
                <span className="text-sm text-purple-200">{Math.round(aluno.score_comportamental)}</span>
              </div>
            </div>
            
            {/* Badges */}
            <div className="flex items-center gap-2 mt-3">
              <Badge className={`${classStyle.bg} ${classStyle.text} border ${classStyle.border} text-xs`}>
                {aluno.classificacao}
              </Badge>
              {aluno.risco_reprovacao !== 'baixo' && (
                <Badge className={`${riscoStyle.bg} text-white text-xs`}>
                  Risco: {riscoStyle.text}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Componente de Linha da Tabela
function AlunoRow({ aluno, onView }) {
  const classStyle = CLASSIFICATION_STYLES[aluno.classificacao] || CLASSIFICATION_STYLES.estavel;
  const riscoStyle = RISCO_STYLES[aluno.risco_reprovacao] || RISCO_STYLES.baixo;
  
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-blue-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };
  
  return (
    <tr 
      className="border-b border-purple-500/10 hover:bg-slate-700/30 transition-colors cursor-pointer"
      onClick={() => onView(aluno)}
      data-testid={`aluno-row-${aluno.id}`}
    >
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          {aluno.foto ? (
            <img 
              src={aluno.foto} 
              alt={aluno.nome}
              className="w-10 h-10 rounded-full object-cover border-2 border-purple-500/30"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-purple-400 flex items-center justify-center text-white font-bold">
              {aluno.nome?.charAt(0) || '?'}
            </div>
          )}
          <div>
            <p className="text-white font-medium">{aluno.nome}</p>
            <p className="text-purple-300/50 text-xs">Mat: {aluno.matricula}</p>
          </div>
        </div>
      </td>
      <td className="py-3 px-4 text-purple-200 text-sm">{aluno.turma}</td>
      <td className="py-3 px-4 text-center">
        <span className={`text-xl font-bold ${getScoreColor(aluno.score_total)}`}>
          {Math.round(aluno.score_total)}
        </span>
      </td>
      <td className="py-3 px-4 text-center text-blue-400">{Math.round(aluno.score_academico)}</td>
      <td className="py-3 px-4 text-center text-green-400">{Math.round(aluno.score_frequencia)}%</td>
      <td className="py-3 px-4 text-center">
        <Badge className={`${classStyle.bg} ${classStyle.text} border ${classStyle.border} text-xs`}>
          {aluno.classificacao}
        </Badge>
      </td>
      <td className="py-3 px-4 text-center">
        {aluno.risco_reprovacao !== 'baixo' && (
          <Badge className={`${riscoStyle.bg} text-white text-xs`}>
            {riscoStyle.text}
          </Badge>
        )}
      </td>
      <td className="py-3 px-4 text-center">
        <Button 
          variant="ghost" 
          size="sm"
          className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/20"
          onClick={(e) => { e.stopPropagation(); onView(aluno); }}
        >
          <Eye className="w-4 h-4" />
        </Button>
      </td>
    </tr>
  );
}

// Componente Principal
export default function ListaAlunos({ turmas, filterRisco = false }) {
  const [loading, setLoading] = useState(true);
  const [alunos, setAlunos] = useState([]);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState('cards'); // cards, table
  
  // Modal do Aluno
  const [selectedAluno, setSelectedAluno] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Filtros
  const [search, setSearch] = useState('');
  const [turmaId, setTurmaId] = useState('all');
  const [classificacao, setClassificacao] = useState('all');
  const [ordenarPor, setOrdenarPor] = useState('nome');
  const [ordem, setOrdem] = useState('asc');
  
  // Paginação
  const [page, setPage] = useState(1);
  const [limit] = useState(24);
  
  const loadAlunos = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit,
        offset: (page - 1) * limit,
        ordenar_por: ordenarPor,
        ordem
      };
      
      if (search) params.search = search;
      if (turmaId !== 'all') params.turma_id = turmaId;
      if (classificacao !== 'all') params.classificacao = classificacao;
      if (filterRisco) params.risco = 'alto';
      
      const endpoint = filterRisco ? '/dnacore/v2/alunos-risco' : '/dnacore/v2/alunos';
      const res = await api.get(endpoint, { params });
      
      setAlunos(res.data.alunos || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
      toast.error('Erro ao carregar lista de alunos');
    } finally {
      setLoading(false);
    }
  }, [search, turmaId, classificacao, ordenarPor, ordem, page, limit, filterRisco]);
  
  useEffect(() => {
    const debounce = setTimeout(() => {
      loadAlunos();
    }, 300);
    
    return () => clearTimeout(debounce);
  }, [loadAlunos]);
  
  const handleViewAluno = (aluno) => {
    setSelectedAluno(aluno);
    setModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedAluno(null);
  };
  
  const totalPages = Math.ceil(total / limit);
  
  return (
    <div className="space-y-6" data-testid="lista-alunos">
      {/* Header com Filtros */}
      <Card className="bg-slate-800/50 border-purple-500/20">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Busca */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-300/50" />
              <Input
                placeholder="Buscar por nome ou matrícula..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-10 bg-slate-700/50 border-purple-500/20 text-white placeholder:text-purple-300/40"
                data-testid="search-input"
              />
            </div>
            
            {/* Filtro Turma */}
            <Select value={turmaId} onValueChange={(v) => { setTurmaId(v); setPage(1); }}>
              <SelectTrigger className="w-[180px] bg-slate-700/50 border-purple-500/20 text-purple-200" data-testid="filter-turma">
                <SelectValue placeholder="Todas as Turmas" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-purple-500/20">
                <SelectItem value="all" className="text-purple-200 focus:bg-purple-500/20">
                  Todas as Turmas
                </SelectItem>
                {turmas.map((t) => (
                  <SelectItem key={t.id} value={t.id} className="text-purple-200 focus:bg-purple-500/20">
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Filtro Classificação */}
            {!filterRisco && (
              <Select value={classificacao} onValueChange={(v) => { setClassificacao(v); setPage(1); }}>
                <SelectTrigger className="w-[160px] bg-slate-700/50 border-purple-500/20 text-purple-200" data-testid="filter-classificacao">
                  <SelectValue placeholder="Classificação" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-purple-500/20">
                  <SelectItem value="all" className="text-purple-200 focus:bg-purple-500/20">
                    Todas
                  </SelectItem>
                  <SelectItem value="excelente" className="text-green-400 focus:bg-green-500/20">
                    Excelente
                  </SelectItem>
                  <SelectItem value="estavel" className="text-blue-400 focus:bg-blue-500/20">
                    Estável
                  </SelectItem>
                  <SelectItem value="atencao" className="text-orange-400 focus:bg-orange-500/20">
                    Atenção
                  </SelectItem>
                  <SelectItem value="critico" className="text-red-400 focus:bg-red-500/20">
                    Crítico
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
            
            {/* Ordenação */}
            <Select value={ordenarPor} onValueChange={setOrdenarPor}>
              <SelectTrigger className="w-[140px] bg-slate-700/50 border-purple-500/20 text-purple-200" data-testid="sort-by">
                <SelectValue placeholder="Ordenar por" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-purple-500/20">
                <SelectItem value="nome" className="text-purple-200 focus:bg-purple-500/20">
                  Nome
                </SelectItem>
                <SelectItem value="score" className="text-purple-200 focus:bg-purple-500/20">
                  Score
                </SelectItem>
                <SelectItem value="frequencia" className="text-purple-200 focus:bg-purple-500/20">
                  Frequência
                </SelectItem>
              </SelectContent>
            </Select>
            
            {/* Direção da Ordenação */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setOrdem(ordem === 'asc' ? 'desc' : 'asc')}
              className="border-purple-500/20 text-purple-300 hover:bg-purple-500/20"
              data-testid="sort-direction"
            >
              {ordem === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
            </Button>
            
            {/* Toggle View Mode */}
            <div className="flex items-center border border-purple-500/20 rounded-lg overflow-hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('cards')}
                className={`rounded-none ${viewMode === 'cards' ? 'bg-purple-500/30 text-white' : 'text-purple-300'}`}
              >
                Cards
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('table')}
                className={`rounded-none ${viewMode === 'table' ? 'bg-purple-500/30 text-white' : 'text-purple-300'}`}
              >
                Tabela
              </Button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-purple-500/10">
            <div className="flex items-center gap-2 text-purple-300/70">
              <Users className="w-4 h-4" />
              <span className="text-sm">{total} aluno(s) encontrado(s)</span>
            </div>
            {filterRisco && (
              <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Filtro: Em Risco
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Lista de Alunos */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="bg-slate-800/50 border-purple-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="w-14 h-14 rounded-full bg-slate-700" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4 bg-slate-700" />
                    <Skeleton className="h-3 w-1/2 bg-slate-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : alunos.length === 0 ? (
        <Card className="bg-slate-800/50 border-purple-500/20">
          <CardContent className="py-16 text-center">
            <Users className="w-16 h-16 text-purple-300/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              Nenhum aluno encontrado
            </h3>
            <p className="text-purple-300/60">
              Ajuste os filtros ou tente uma busca diferente
            </p>
          </CardContent>
        </Card>
      ) : viewMode === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="alunos-grid">
          {alunos.map((aluno) => (
            <AlunoCard key={aluno.id} aluno={aluno} onView={handleViewAluno} />
          ))}
        </div>
      ) : (
        <Card className="bg-slate-800/50 border-purple-500/20 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="alunos-table">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="py-3 px-4 text-left text-purple-300 font-semibold text-sm">Aluno</th>
                  <th className="py-3 px-4 text-left text-purple-300 font-semibold text-sm">Turma</th>
                  <th className="py-3 px-4 text-center text-purple-300 font-semibold text-sm">Score</th>
                  <th className="py-3 px-4 text-center text-purple-300 font-semibold text-sm">Acadêmico</th>
                  <th className="py-3 px-4 text-center text-purple-300 font-semibold text-sm">Frequência</th>
                  <th className="py-3 px-4 text-center text-purple-300 font-semibold text-sm">Classificação</th>
                  <th className="py-3 px-4 text-center text-purple-300 font-semibold text-sm">Risco</th>
                  <th className="py-3 px-4 text-center text-purple-300 font-semibold text-sm">Ações</th>
                </tr>
              </thead>
              <tbody>
                {alunos.map((aluno) => (
                  <AlunoRow key={aluno.id} aluno={aluno} onView={handleViewAluno} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      
      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="border-purple-500/20 text-purple-300 hover:bg-purple-500/20 disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              
              return (
                <Button
                  key={pageNum}
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(pageNum)}
                  className={`w-8 border-purple-500/20 ${
                    page === pageNum 
                      ? 'bg-purple-500 text-white border-purple-500' 
                      : 'text-purple-300 hover:bg-purple-500/20'
                  }`}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="border-purple-500/20 text-purple-300 hover:bg-purple-500/20 disabled:opacity-50"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          
          <span className="text-purple-300/50 text-sm ml-2">
            Página {page} de {totalPages}
          </span>
        </div>
      )}
      
      {/* Modal de Detalhes do Aluno */}
      <ModalAluno 
        open={modalOpen} 
        onClose={handleCloseModal} 
        aluno={selectedAluno} 
      />
    </div>
  );
}
